import React, { useState, useMemo } from 'react';
import { useApp, hasPrefix, previewDocNo } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import PrintPreview from '../common/PrintPreview';
import { Plus, AlertTriangle, Edit2, Trash2, Printer } from 'lucide-react';
import { formatCurrency, formatDate, today } from '../../utils/helpers';
import { validators, validateForm } from '../../utils/validation';

const emptyForm = { date: today(), expenseHead: 'Rent', amount: 0, paidFrom: 'Cash', bankAccount: '', billRef: '', tdsSectionId: '', narration: '' };

export default function ExpenseEntry() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const payAccount = form.paidFrom === 'Cash'
    ? state.accounts.find(a => a.name === 'Cash')
    : state.accounts.find(a => a.name === form.bankAccount);
  const payAccountName = payAccount?.name || (form.paidFrom === 'Cash' ? 'Cash' : 'Bank');

  // TDS: deduct tax at source. Expense is booked at gross; only the net leaves
  // the bank/cash, and the TDS becomes a payable to the government.
  const tdsSection = (state.tdsSections || []).find(t => t.id === form.tdsSectionId);
  const tdsRate = tdsSection?.rate || 0;
  const tdsAmount = tdsSection ? Math.round((Number(form.amount) || 0) * tdsRate) / 100 : 0;
  const netPayable = (Number(form.amount) || 0) - tdsAmount;

  const insufficientFunds = payAccount && netPayable > payAccount.balance;

  const errors = useMemo(() => {
    const { errors } = validateForm({
      date: [validators.required(form.date, 'Date')],
      expenseHead: [validators.required(form.expenseHead, 'Expense head')],
      amount: [
        validators.required(form.amount, 'Amount'),
        validators.positiveNonZero(form.amount, 'Amount'),
        validators.maxValue(form.amount, 99999999, 'Amount'),
      ],
      billRef: [validators.maxLength(form.billRef, 100, 'Bill reference')],
      narration: [validators.maxLength(form.narration, 500, 'Narration')],
      ...(form.paidFrom === 'Bank' ? { bankAccount: [validators.required(form.bankAccount, 'Bank account')] } : {}),
    });
    return errors;
  }, [form]);

  const hasErrors = Object.values(errors).some(e => e);
  const touch = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const prefixMissing = !editData && !hasPrefix(state, 'expense', form.date);

  const handleSave = async () => {
    if (prefixMissing) { alert('No Prefix Setting found for Expense. Add one in Masters → Prefix Setting (effective on the selected date) before saving.'); return; }
    setSubmitted(true);
    setTouched({ date: true, amount: true, expenseHead: true, bankAccount: true });
    if (hasErrors) return;
    if (insufficientFunds) {
      const ok = await confirm(`${payAccountName} balance (${formatCurrency(payAccount?.balance || 0)}) is less than expense amount. The balance will go negative.`, { title: 'Low Balance', variant: 'warning', confirmLabel: 'Continue Anyway' });
      if (!ok) return;
    }
    setSaving(true);
    const payload = { ...form, tdsSection: tdsSection?.section || '', tdsRate, tdsAmount, netAmount: netPayable };
    try {
      dispatch({ type: 'ADD_EXPENSE', payload });
      addToast('Expense saved successfully', 'success');
    } catch (e) {
      addToast('Failed to save expense', 'error');
    }
    if (editData) {
      dispatch({ type: 'DELETE_EXPENSE', payload: editData.id });
    }
    closeForm();
  };

  const handleEdit = (row) => {
    setForm({ date: row.date, expenseHead: row.expenseHead, amount: row.amount, paidFrom: row.paidFrom, bankAccount: row.bankAccount || '', billRef: row.billRef || '', tdsSectionId: row.tdsSectionId || '', narration: row.narration || '' });
    setEditData(row);
    setTouched({});
    setSubmitted(false);
    setShowForm(true);
  };

  const handleDelete = (row) => {
    if (window.confirm(`Delete expense ${row.expenseNo}? Account balance will be reversed.`)) {
      dispatch({ type: 'DELETE_EXPENSE', payload: row.id });
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(emptyForm);
    setEditData(null);
    setTouched({});
    setSubmitted(false);
  };

  const columns = [
    { key: 'expenseNo', label: 'Expense No', sortable: true },
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'expenseHead', label: 'Head', render: (v) => <span className="badge badge-gray">{v}</span> },
    { key: 'amount', label: 'Amount', align: 'right', render: (v) => formatCurrency(v) },
    { key: 'paidFrom', label: 'Paid From' },
    { key: 'narration', label: 'Narration' },
  ];

  if (printData) return <PrintPreview type="expense" data={printData} onClose={() => setPrintData(null)} />;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Expense Entries</h1>
        <button onClick={() => { setForm(emptyForm); setEditData(null); setTouched({}); setSubmitted(false); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Expense
        </button>
      </div>
      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.expenses].reverse()}
          searchFields={['expenseNo', 'expenseHead', 'narration']}
          emptyState={{ title: 'No expenses yet', description: 'Record your first expense entry.', actionLabel: 'New Expense', onAction: () => { setForm(emptyForm); setTouched({}); setSubmitted(false); setShowForm(true); } }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => setPrintData(row)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Print"><Printer size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={closeForm} title={editData ? `Edit Expense ${editData.expenseNo}` : 'New Expense Entry'}>
        <ErrorSummary errors={errors} show={submitted && hasErrors} />

        {insufficientFunds && form.amount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800 flex items-center gap-2 mb-4">
            <AlertTriangle size={16} />
            {payAccountName} balance ({formatCurrency(payAccount?.balance || 0)}) is less than expense amount
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Expense No">
              <input className="input bg-gray-100 font-semibold text-blue-700" value={editData ? editData.expenseNo : previewDocNo(state, 'expense', 'expenses', form.date, 'EXP')} readOnly placeholder="Set Prefix first" />
            </FormField>

            <FormField label="Date" required error={errors.date} touched={touched.date || submitted}>
              <input className="input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} onBlur={() => touch('date')} max={today()} />
            </FormField>

            <FormField label="Expense Head" required error={errors.expenseHead} touched={touched.expenseHead || submitted}>
              <select className="input" value={form.expenseHead} onChange={e => setForm({...form, expenseHead: e.target.value})}>
                <option>Rent</option>
                <option>Salary</option>
                <option>Transport</option>
                <option>Office Expenses</option>
                <option>Miscellaneous</option>
              </select>
            </FormField>

            <FormField label="Amount" required error={errors.amount} touched={touched.amount || submitted}>
              <input className="input" type="number" min="0.01" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: Number(e.target.value)})} onBlur={() => touch('amount')} />
            </FormField>

            <FormField label="Paid From" required hint={payAccount ? `Balance: ${formatCurrency(payAccount.balance)}` : ''}>
              <select className="input" value={form.paidFrom} onChange={e => setForm({...form, paidFrom: e.target.value, bankAccount: e.target.value === 'Cash' ? '' : form.bankAccount})}>
                <option>Cash</option>
                <option>Bank</option>
              </select>
            </FormField>

            {form.paidFrom === 'Bank' && (
              <FormField label="From Bank" required error={errors.bankAccount} touched={touched.bankAccount || submitted} hint="Bank account this expense is paid from">
                <select className="input" value={form.bankAccount} onChange={e => setForm({...form, bankAccount: e.target.value})} onBlur={() => touch('bankAccount')}>
                  <option value="">-- Select Bank --</option>
                  {state.accounts.filter(a => a.type === 'Bank').map(a => (
                    <option key={a.id} value={a.name}>{a.name} (Balance: {formatCurrency(a.balance)})</option>
                  ))}
                </select>
              </FormField>
            )}

            <FormField label="Bill Reference" error={errors.billRef} touched={touched.billRef || submitted}>
              <input className="input" value={form.billRef} onChange={e => setForm({...form, billRef: e.target.value})} onBlur={() => touch('billRef')} maxLength={100} placeholder="Optional bill/receipt number" />
            </FormField>

            <FormField label="TDS Section" hint="Deduct tax at source (optional)">
              <select className="input" value={form.tdsSectionId} onChange={e => setForm({...form, tdsSectionId: e.target.value})}>
                <option value="">-- No TDS --</option>
                {(state.tdsSections || []).map(t => (
                  <option key={t.id} value={t.id}>{t.section} · {t.name} ({t.rate}%)</option>
                ))}
              </select>
            </FormField>
          </div>

          {tdsSection && (
            <div className="grid grid-cols-3 gap-3 p-3 bg-blue-50 rounded-lg text-sm">
              <div><span className="text-gray-500 block text-xs">Gross Expense</span><span className="font-semibold">{formatCurrency(Number(form.amount) || 0)}</span></div>
              <div><span className="text-gray-500 block text-xs">TDS @ {tdsRate}% ({tdsSection.section})</span><span className="font-semibold text-red-600">− {formatCurrency(tdsAmount)}</span></div>
              <div><span className="text-gray-500 block text-xs">Net Payable</span><span className="font-bold text-green-700">{formatCurrency(netPayable)}</span></div>
            </div>
          )}

          <FormField label="Narration" error={errors.narration} touched={touched.narration || submitted}>
            <textarea className="input" rows={2} value={form.narration} onChange={e => setForm({...form, narration: e.target.value})} onBlur={() => touch('narration')} maxLength={500} placeholder="Description of expense" />
          </FormField>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeForm} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={(submitted && hasErrors) || saving}>{editData ? 'Update' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}
